import { Flex, IconButton, Tooltip } from "@evergis/ui";
import DOMPurify from "dompurify";
import React, { FC, useCallback, useMemo, useRef, useState } from "react";

const TOOLTIP_ANCHOR_EXIT_DELAY = 600;
const TOOLTIP_EXIT_DELAY = 300;

export type AttributeFieldTitleProps = {
  description: string;
  title: string;
};

export const AttributeFieldTitle: FC<AttributeFieldTitleProps> = ({ description, title }) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();
  const onAnchorMouseEnter = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    setIsVisible(true);
  }, []);
  const onAnchorMouseLeave = useCallback(() => {
    timeoutId.current = setTimeout(() => {
      setIsVisible(false);
    }, TOOLTIP_ANCHOR_EXIT_DELAY);
  }, []);
  const onContentMouseEnter = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
  }, []);
  const onContentMouseLeave = useCallback(() => {
    timeoutId.current = setTimeout(() => {
      setIsVisible(false);
    }, TOOLTIP_EXIT_DELAY);
  }, []);

  const tooltipContent = useMemo(
    () =>
      description && (
        <div
          style={{ pointerEvents: "all" }}
          onMouseEnter={onContentMouseEnter}
          onMouseLeave={onContentMouseLeave}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              description.replace(
                // eslint-disable-next-line no-useless-escape
                /\[([^\[]+)\](\(([^)]*))\)/gim,
                // eslint-disable-next-line @typescript-eslint/quotes, quotes
                '<a href="$3" target="_blank" rel = ”noopener”>$1</a>',
              ),
            ),
          }}
        />
      ),
    [description, onContentMouseEnter, onContentMouseLeave],
  );

  if (!description) {
    return <>{title}</>;
  }

  return (
    <Flex style={{ alignItems: "center" }}>
      {title}
      <Tooltip trigger="manual" hideOnClick={false} isVisible={isVisible} arrow dark content={tooltipContent}>
        {ref => (
          <IconButton
            onMouseEnter={onAnchorMouseEnter}
            onMouseLeave={onAnchorMouseLeave}
            kind={"info"}
            innerRef={ref}
          />
        )}
      </Tooltip>
    </Flex>
  );
};
